import { useSubmit } from "@certn/api";
import { Stack, Text, showToast } from "@certn/cascadia";
import {
  Container,
  Icon,
  mq,
  Text as StepHeadingText,
} from "@certn/cascadia-react";
import {
  EVENT_NAMES,
  RegionContext,
  RegionProvider,
  useEventTracking,
} from "@certn/utils";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { OrderReviewEmailPreview } from "../../../components/ordering-review/OrderReviewEmailPreview";
import { OrderReviewSummaryBox } from "../../../components/ordering-review/OrderReviewSummaryBox";
import { WwccLimitationsMessage } from "../../../components/ordering-review/WwccLimitationsMessage";
import { ConsumerReportCertificationSection } from "../../../components/ordering-review/ConsumerReportCertificationSection";
import { useUser } from "../../../hooks/useUser";
import { OrderContext } from "../OrderContext";
import { clearOrderDraft, useOrderDraftKey } from "../orderDraftStorage";
import { OrderStepLayout } from "../components/OrderStepLayout";
import {
  PlaceOrderDisabledReasons,
  getPlaceOrderDisabledReasons,
} from "../components/PlaceOrderDisabledReasons";
import {
  enabledNotificationChannels,
  orderContextToSubmitOrderPayload,
  orderIncludesAuWwcc,
  orderRequiresEmploymentConsumerReportCertification,
  showSubmitOrderError,
} from "../order-utils";
import { useCanOrderCVReview } from "../../../components/cv-review/utils";
import { CVReviewExtraction } from "../../../components/cv-review/CVReviewExtraction";
import { useFocusStepHeading } from "../useFocusStepHeading";

export function ReviewStepApplicantPay() {
  const { order, goToNextStep } = useContext(OrderContext);
  const { t } = useTranslation();
  const stepHeadingId = useFocusStepHeading();
  const { applicantSmsMessaging, applicantWhatsappMessaging } = useFlags();
  const { details: userDetails } = useUser();
  const orderDraftKey = useOrderDraftKey();
  const { trackEvent } = useEventTracking();
  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/submit-order/`,
    {
      throwErrors: true,
    }
  );

  const [
    consumerReportCertificationAcknowledged,
    setConsumerReportCertificationAcknowledged,
  ] = useState(false);
  const certificationRequired =
    orderRequiresEmploymentConsumerReportCertification(order, userDetails);
  const [disableCompletion, setDisableCompletion] = useState<boolean>(false);

  const { canOrderCVReview, templateId, verificationType } =
    useCanOrderCVReview({
      regularChecks: order.checks,
      packageChecks: order.packageData?.checks || [],
    });

  const submitOrder = async () => {
    try {
      await submit(
        orderContextToSubmitOrderPayload(order, userDetails, {
          consumerReportCertificationAcknowledged:
            certificationRequired && consumerReportCertificationAcknowledged,
          enabledNotificationChannels: enabledNotificationChannels({
            applicantSmsMessaging,
            applicantWhatsappMessaging,
          }),
        })
      );
      trackEvent(EVENT_NAMES.CP_SUBMITTED_PAYMENT, {
        paymentMethod: "applicant",
        checksOrdered: order.checks.map((c) => c.key).join(","),
      });
      clearOrderDraft(orderDraftKey);
      showToast(t("Order submitted"), { type: "info" });
      goToNextStep();
    } catch (e) {
      showSubmitOrderError(e, t);
    }
  };

  const disabledReasons = getPlaceOrderDisabledReasons({
    hasNoRecipients: order.recipients.length === 0,
    isCertificationUnacknowledged:
      certificationRequired && !consumerReportCertificationAcknowledged,
    isCvReviewInvalid: disableCompletion,
  });

  return (
    <OrderStepLayout
      onComplete={submitOrder}
      disableCompletion={disabledReasons.length > 0}
      submitting={isSubmitting}
      scrollRegionAriaLabel={t("Order review content")}
    >
      <Container layout="wide">
        <StepHeadingText
          id={stepHeadingId}
          as="h1"
          tabIndex={-1}
          style="global/title-small"
        >
          {t("Review and send")}
        </StepHeadingText>

        <div className={styles.page}>
          <Stack className={styles.summary} gap="space-4">
            <PlaceOrderDisabledReasons reasons={disabledReasons} />
            <Text style="global/body-secondary" color="text-weak">
              {t(
                "Payments will be charged to the applicant when the case completes. The applicant will receive receipts for the checks when they have been completed."
              )}
            </Text>
            <div className={styles.clientResponsibilityBox}>
              <Icon
                name="warning"
                type="standard"
                color="icon-warning"
                size="space-6"
              />
              <div className={styles.clientResponsibilityContent}>
                <Text style="global/heading-small">
                  {t("Client responsibility")}
                </Text>
                <Text
                  style="global/body-secondary"
                  className={styles.clientResponsibilityText}
                >
                  {t(
                    "By placing this order, you are responsible for ensuring compliance with all applicable laws and regulations governing applicant-paid background screening services. Please note that many jurisdictions restrict or prohibit the ability for companies relying on background screening results to require individuals to pay for such services."
                  )}
                </Text>
              </div>
            </div>
            <div className={styles.orderSummary}>
              <RegionProvider>
                <RegionContext.Consumer>
                  {(region) => (
                    <OrderReviewSummaryBox order={order} region={region} />
                  )}
                </RegionContext.Consumer>
              </RegionProvider>
            </div>
            {orderIncludesAuWwcc(order) && <WwccLimitationsMessage />}
            {certificationRequired && (
              <ConsumerReportCertificationSection
                acknowledged={consumerReportCertificationAcknowledged}
                onAcknowledgedChange={
                  setConsumerReportCertificationAcknowledged
                }
              />
            )}
            {canOrderCVReview && order.cv_review_id && (
              <CVReviewExtraction
                cv_review_id={order.cv_review_id}
                templateId={templateId}
                verificationType={verificationType}
                setDisableCompletion={setDisableCompletion}
              />
            )}
            <OrderReviewEmailPreview order={order} />
          </Stack>
          <div className={styles.orderSummaryWide}>
            <RegionProvider>
              <RegionContext.Consumer>
                {(region) => (
                  <OrderReviewSummaryBox
                    order={order}
                    region={region}
                    border="dropshadow"
                  />
                )}
              </RegionContext.Consumer>
            </RegionProvider>
          </div>
        </div>
      </Container>
    </OrderStepLayout>
  );
}

const styles = {
  page: css({
    display: "flex",
    gap: "var(--space-6)",
    flexWrap: "wrap-reverse",
  }),
  summary: css({ flex: "1 1 540px", minWidth: 0 }),
  clientResponsibilityBox: css({
    display: "flex",
    gap: "var(--space-2)",
    padding: "var(--space-4)",
    paddingBottom: "var(--space-6)",
    backgroundColor: "var(--background-warning-weak)",
    borderRadius: "var(--border-radius-2)",
    overflow: "hidden",
  }),
  clientResponsibilityContent: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
    flex: 1,
  }),
  clientResponsibilityText: css({
    marginTop: "var(--space-1)",
  }),
  orderSummary: css(
    { flexBasis: 304 },
    mq({ display: ["block", "block", "none"] })
  ),
  orderSummaryWide: css(
    { flex: "1 1 304px" },
    mq({ display: ["none", "none", "block"] })
  ),
};
